import React, { useEffect, useState } from 'react';
import { FaLink, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { parseBulkVideoLinks, segmentPlacementLabel } from './bulkLinkImportUtils';

/**
 * TEMPORARY optional UI — timeline bulk link import.
 * Duration probing lives in bulkLinkResolve.js (frontend). Disable via ENABLE_BULK_LINK_FRONTEND_RESOLVE.
 */
export default function BulkClipLinkImport({
  characters = [],
  defaultCharacter = '',
  segments = [],
  tagPrefix = 'clip',
  busy = false,
  onImport,
  open: openControlled,
  onOpenChange,
  placement: placementControlled,
  onPlacementChange,
}) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openControlled ?? openInternal;
  const setOpen = onOpenChange ?? setOpenInternal;

  const [character, setCharacter] = useState(defaultCharacter || characters[0] || '');
  const [linksText, setLinksText] = useState('');
  const [prefix, setPrefix] = useState(tagPrefix);

  const [placementInternal, setPlacementInternal] = useState({
    mode: 'end',
    index: 0,
  });
  const placement = placementControlled ?? placementInternal;
  const setPlacement = onPlacementChange ?? setPlacementInternal;

  useEffect(() => {
    setCharacter(defaultCharacter || characters[0] || '');
  }, [defaultCharacter, characters]);

  useEffect(() => {
    if (placementControlled) return;
    setPlacementInternal({
      mode: segments.length ? 'after' : 'end',
      index: Math.max(0, segments.length - 1),
    });
  }, [segments.length, placementControlled]);

  const linkCount = parseBulkVideoLinks(linksText).length;
  const total = segments.length;

  const handleSubmit = (e) => {
    e.preventDefault();
    onImport?.({
      character: character || characters[0] || '',
      linksText,
      tagPrefix: (prefix || 'clip').trim() || 'clip',
      placement: placement.mode === 'end'
        ? { mode: 'end', index: total - 1 }
        : { mode: placement.mode, index: placement.index },
    });
  };

  return (
    <div className={`bulk-clip-import bulk-clip-import--compact ${open ? 'bulk-clip-import--open' : ''}`}>
      <button
        type="button"
        className="bulk-clip-import__toggle"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        title="Bulk import video links (optional)"
      >
        <FaLink aria-hidden="true" />
        <span>Bulk links</span>
        {!open && linkCount > 0 && (
          <span className="bulk-clip-import__badge">{linkCount} ready</span>
        )}
        {open ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
      </button>

      {open && (
        <form className="bulk-clip-import__panel" onSubmit={handleSubmit}>
          <details className="bulk-clip-import__details">
            <summary>What this does</summary>
            <p className="bulk-clip-import__note">
              URLs in order → full-length Videos clips (tagged) + matching <code>[CLIP:tag]</code> timeline rows.
            </p>
          </details>

          <div className="bulk-clip-import__row bulk-clip-import__row--3">
            <label>
              Insert
              <select
                value={placement.mode}
                onChange={e => setPlacement(p => ({ ...p, mode: e.target.value }))}
                disabled={busy}
              >
                <option value="end">At end</option>
                <option value="after">After segment</option>
                <option value="before">Before segment</option>
              </select>
            </label>
            {placement.mode !== 'end' && total > 0 && (
              <label>
                Segment
                <select
                  value={String(placement.index)}
                  onChange={e => setPlacement(p => ({ ...p, index: Number(e.target.value) }))}
                  disabled={busy}
                >
                  {segments.map((seg, idx) => (
                    <option key={idx} value={idx}>
                      {segmentPlacementLabel(idx, seg, total)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <label>
              Character
              <select
                value={character}
                onChange={e => setCharacter(e.target.value)}
                disabled={busy || !characters.length}
              >
                {!characters.length && <option value="">— add character —</option>}
                {characters.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="bulk-clip-import__row">
            <label>
              Tag prefix
              <input
                type="text"
                value={prefix}
                onChange={e => setPrefix(e.target.value)}
                placeholder="clip"
                disabled={busy}
              />
            </label>
          </div>

          <label className="bulk-clip-import__links-label">
            Links ({linkCount})
            <textarea
              rows={4}
              value={linksText}
              onChange={e => setLinksText(e.target.value)}
              placeholder={'One URL per line\nhttps://…'}
              disabled={busy}
            />
          </label>

          <div className="bulk-clip-import__actions">
            <button type="button" className="bulk-clip-import__cancel" onClick={() => setOpen(false)} disabled={busy}>
              Minimize
            </button>
            <button type="submit" className="btn-save bulk-clip-import__submit" disabled={busy || !linkCount || !character}>
              {busy ? 'Importing…' : `Import ${linkCount || ''}`}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

/** Small control on a timeline row — open bulk panel anchored to this segment. */
export function BulkClipLinkAnchorButton({ index, mode = 'after', disabled, onActivate }) {
  const label = mode === 'before' ? 'Bulk links before' : 'Bulk links after';
  return (
    <button
      type="button"
      className="control-button bulk-clip-import__row-trigger"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={() => onActivate?.({ mode, index })}
    >
      <FaLink className="icon add-icon" aria-hidden="true" />
    </button>
  );
}
